<template>
  <div
    ref="stepRef"
    class="dvp-modal-wrapper"
    :class="mainClasses"
  >
    <div
      role="document"
      class="dvp-modal appearance-surface"
      :class="classes"
    >
      <!--{{ fromHeight }}-->
      <div class="dvp-modal__top">
        <basic-close-button
          v-if="$props.closeable"
          aria-label="Close Modal"
          :size="closeButtonSize"
          @click="close()"
        />
      </div>

      <slot />
    </div>
  </div>
</template>

<script setup>
  import { computed, inject, onBeforeMount, onMounted, provide, ref } from 'vue'

  import events$ from '@shared/services/Events'

  import BasicCloseButton from '@shared/components/Basic/CloseButton'

  import { EVENT_MODAL } from '@shared/config/events'

  // INIT
  const animation = ref({
    timeout: 300,
    css: '300ms',
  })
  const close = inject('close')
  const modalId = inject('id')
  const doIt = inject('doIt')
  const isInitialOpen = inject('isInitialOpen')
  const props = defineProps({
    closeable: {
      type: Boolean,
      default: true,
    },

    hasGradient: {
      type: Boolean,
      default: true,
    },

    id: {
      type: String,
      required: true,
    },

    modalActions: {
      type: Boolean,
      default: true,
    },

    primaryLabel: {
      type: String,
      default: null,
    },

    primaryTrackAs: {
      type: String,
      default: null,
    },

    primaryVisible: {
      type: Boolean,
      default: true,
    },

    secondaryLabel: {
      type: String,
      default: null,
    },

    secondaryTrackAs: {
      type: String,
      default: null,
    },

    secondaryVisible: {
      type: Boolean,
      default: true,
    },

    tertiaryLabel: {
      type: String,
      default: null,
    },

    size: {
      validator(value) {
        return ['mini', 'midi', 'maxi', 'full'].includes(value)
      },
      default: 'midi',
    },

    stepTitle: {
      type: String,
      default: null,
    },

    type: {
      /*
       *  passive:            no CTA
       *  transactional:      validate decisions, requires action to be completed and closed
       *  acknowledgement:    system requires the user to acknowledge the presented information
       *  progress:           navigate via next and back
       *  raw:
       */
      validator(value) {
        return ['passive', 'transactional', 'acknowledgement', 'progress', 'raw'].includes(value)
      },
      default: 'passive',
    },
  })

  // DATA
  const stepRef = ref()
  const fromHeight = ref()

  // COMPUTED
  const classes = computed(() => {
    return {
      [`dvp-modal--${props.size}`]: true,
      [`dvp-modal--${props.type}`]: true,
    }
  })

  const closeButtonSize = computed(() => {
    return ['maxi', 'full'].includes(props.size) ? 'lg' : 'sm'
  })

  const mainClasses = computed(() => {
    return {
      'animation-initial': isInitialOpen.value === true,
      'animation-step': isInitialOpen.value === false,
      'the-grid': !['maxi', 'full'].includes(props.size),
      [`dvp-modal-wrapper--${props.size}`]: true,
    }
  })

  // METHODS

  // LIFECYCLE HOOKS
  onBeforeMount(async () => {
    // @note: set the initialFlag to flag, used for animations
    if (isInitialOpen.value) {
      window.setTimeout(() => {
        isInitialOpen.value = false
      }, animation.value.timeout)
    }

    events$.emit(EVENT_MODAL.UPDATE, {
      modalName: modalId + '-' + props.id,
    })

    await new Promise(r => setTimeout(r, 100))

    fromHeight.value = doIt(true)
  })

  onMounted(() => {
    fromHeight.value = doIt()
  })

  // EPILOGUE
  provide('closeable', props.closeable)
  provide('hasGradient', props.hasGradient)
  provide('stepId', props.id)
  provide('size', props.size)
  provide('stepTitle', props.stepTitle)
  provide('modalActions', props.modalActions)
  provide('type', props.type)
</script>

<style name="animations" scoped>
  .step-enter-active {
    transition-duration: 1s;
  }

  .step-leave-active {
    transition-duration: 1s;
  }

  .step-leave-active :deep(.dvp-modal__footer),
  .step-leave-active :deep(.dvp-modal__top) {
    transition: all .3s ease;
  }

  .step-leave-active :deep(.scrollable) {
    transition: all .3s ease;
    transition-delay: .3s;
  }

  .step-enter-active :deep(.scrollable) {
    transition: all .3s ease;
  }

  .step-enter-active :deep(.dvp-modal__footer),
  .step-enter-active :deep(.dvp-modal__top) {
    transition: all .3s ease;
    transition-delay: .3s;
  }

  .step-enter-from :deep(.scrollable) {
    transform: translateX(100%);
  }

  .step-leave-to :deep(.scrollable) {
    transform: translateX(-100%);
  }

  .step-enter-from :deep(.dvp-modal__footer) {
    transform: translateY(100%);
  }

  .step-leave-to :deep(.dvp-modal__footer) {
    transform: translateY(100%);
  }

  .step-enter-from :deep(.dvp-modal__top) {
    transform: translateY(-100%);
  }

  .step-leave-to :deep(.dvp-modal__top) {
    transform: translateY(-100%);
  }
</style>

<style scoped>
  .dvp-modal-wrapper {
    position: relative;
    height: 100%;
    align-items: center;

    &:not(.the-grid) {
      display: flex;
      align-items: flex-end;
      width: 100%;
    }
  }

  .dvp-modal {
    display: flex;
    flex-direction: column;
    z-index: var(--dvp-stack-level-popup-menu);
    background-color: var(--surface);
    box-shadow: var(--elevation-level-3);
    border-radius: var(--modal-border-radius);
    overflow: hidden;

    &--mini {
      --modal-border-radius: var(--fixed-border-radius-fix-03);

      grid-column: 1 / -1;
      max-height: 48dvh;
    }

    &--midi {
      --modal-border-radius: var(--fixed-border-radius-fix-03);

      grid-column: 1 / -1;
      max-height: 72dvh;
    }

    &--maxi {
      --modal-border-radius: var(--border-radius-res-l) var(--border-radius-res-l) var(--fixed-border-radius-fix-00)
        var(--fixed-border-radius-fix-00);

      width: 100%;
      max-height: 96dvh;
    }

    &--full {
      display: inline-block;
      width: 100%;
      height: 100dvh;
    }

    &__top {
      display: flex;
      justify-content: flex-end;
      padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive)
        var(--dotcom-responsive-spacing-res-xs);
    }

    &__actions {
      padding: var(--fixed-spacing-fix-02) var(--dotcom-responsive-gutter-responsive)
        var(--dotcom-responsive-gutter-responsive);

      .transactional,
      .passive {
        display: flex;
        flex-direction: column;
        gap: var(--fixed-spacing-fix-02);
      }

      .acknowledgement {
        display: flex;
        justify-content: center;
      }

      .progress {
        display: flex;
        flex-direction: column;
        gap: var(--fixed-spacing-fix-02);

        &__navigation {
          display: flex;
          flex-direction: column;
          gap: var(--fixed-spacing-fix-02);
        }
      }

      &.raw {
        display: flex;
        flex-direction: column;
        gap: var(--fixed-spacing-fix-02);
        justify-content: space-between;
      }
    }
  }

  .dvp-modal--maxi:deep(.scrollable),
  .dvp-modal--full:deep(.scrollable) {
    padding: 0;
  }

  .dvp-modal__title {
    margin: 0 0 var(--dotcom-responsive-spacing-res-xxs) 0;
  }

  .dvp-modal--mini .dvp-modal__title {
    margin: 0 0 var(--fixed-spacing-fix-02) 0;
  }

  .dvp-modal--maxi .dvp-modal__title,
  .dvp-modal--maxi .dvp-modal__body,
  .dvp-modal--full .dvp-modal__title,
  .dvp-modal--full .dvp-modal__body {
    grid-column: 1 / -1;
  }

  .dvp-modal__top {
    display: flex;
    justify-content: flex-end;
    padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive)
      var(--dotcom-responsive-spacing-res-xs);
  }

  .dvp-modal__footer.the-grid > * {
    grid-column: 1 / -1;
  }

  .dvp-modal__footer.the-grid .dvp-modal__actions {
    padding: var(--fixed-spacing-fix-02) 0 var(--dotcom-responsive-gutter-responsive);
  }

  @media (--v-medium) {
    .dvp-modal {
      &--mini {
        width: 320px;
        margin: 0 auto;
      }

      &__actions {
        .passive {
          flex-direction: row;
          justify-content: flex-end;
        }

        .transactional,
        &.raw {
          flex-direction: row-reverse;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--fixed-spacing-fix-06);
        }
      }
    }
  }

  @media (--v-large) {
    .dvp-modal {
      &--midi {
        grid-column: 3 / 11;
      }

      &__actions {
        .acknowledgement {
          justify-content: flex-end;
        }

        .progress {
          flex-direction: row-reverse;
          justify-content: space-between;
          gap: var(--fixed-spacing-fix-06);

          &__navigation {
            flex-direction: row-reverse;
            gap: var(--fixed-spacing-fix-06);
          }
        }
      }
    }
  }

  @media (--v-wide) {
    .dvp-modal {
      &--mini {
        width: auto;
        margin: 0;
        grid-column: 5 / 9;
      }
    }
  }
</style>
